<template>
  <div
    data-cy="marketing-edit-advert-communication-tab"
    :data-view-ready="!loading">
    <div
      v-if="loading"
      class="col-12 text-center">
      <coozzy-spinner />
    </div>
    <template v-else>
      <coozzy-card class="global-card mb-3">
        <h5 class="cursor-pointer">
          {{ $t('message.marketingObjects.editAdvert.tabs.communication') }}
        </h5>
        <div class="row mb-2">
          <div class="col-12">
            <label>{{ $t('message.tableColumnHeadings.adminAssignee') }} </label>
            <coozzy-form-select
              v-model="contactIdAssigneeMarketing"
              data-cy="marketing-edit-advert-assignee-select"
              class="select-entries float-right"
              name="assigneeMarketing">
              <option :value="null">
                -
              </option>
              <option
                v-for="(employee, index) in availableEmployeeContacts"
                :key="index"
                :value="employee.id">
                {{ employee.firstName }} {{ employee.lastName }}
              </option>
            </coozzy-form-select>
          </div>
          <div class="col-12 mt-2">
            <coozzy-form-checkbox
              v-model="publishAssigneePhone"
              data-cy="marketing-edit-advert-publish-assignee-phone-checkbox"
              :initial="publishAssigneePhone"
              name="publishAssigneePhone">
              {{ $t('message.advertisement.publishAssigneePhone') }}
            </coozzy-form-checkbox>
          </div>
          <div class="col-12 mt-2">
            <viewing-appointment-contact-section
              ref="viewingContactSection"
              class="mt-3"
              :object="object"
              :building="building"
              :contact-id-assignee-marketing="contactIdAssigneeMarketing"
              :lock-contact-selection="!viewingAppointmentsLoaded || (hasViewingAppointments && isViewingContactSelected)"
              :show-contact-lock-tooltip="viewingAppointmentsLoaded && hasViewingAppointments && isViewingContactSelected"
              @viewing-appointment-updated="onViewingAppointmentUpdated"
              @viewing-contact-updated="onViewingContactUpdated"
              @contact-selection-changed="onContactSelectionChanged"
              @viewing-random-changed="onViewingRandomChanged"
              @contacts-resolved="onViewingContactsResolved" />
          </div>
        </div>
        <hr>
        <div class="row">
          <div class="col-12 mt-3">
            <label>{{ $t('message.advertisement.defaultAnswer') }}</label>
            <div class="row px-3 mt-3">
              <coozzy-form-radio
                v-model="defaultAnswerType"
                data-cy="marketing-edit-advert-default-answer-template-radio"
                :default-model="defaultAnswerType"
                class="col"
                name="defaultAnswerType"
                select-value="Template">
                {{ $t('message.calendar.event.template') }}
              </coozzy-form-radio>
              <coozzy-form-radio
                v-model="defaultAnswerType"
                data-cy="marketing-edit-advert-default-answer-none-radio"
                :default-model="defaultAnswerType"
                class="col"
                name="defaultAnswerType"
                select-value="None">
                {{ $t('message.contact.types.NONE') }}
              </coozzy-form-radio>
            </div>
          </div>
          <div
            v-if="defaultAnswerType === 'Template'"
            class="col-12 mt-3">
            <div class="row">
              <div class="col-12">
                <coozzy-form-checkbox
                  v-model="defaultAnswer.includeViewingAppointmentContact"
                  data-cy="marketing-edit-advert-include-contact-data-checkbox"
                  :initial="defaultAnswer.includeViewingAppointmentContact"
                  name="includeContactDataInDefaultAnswer">
                  {{ $t('message.advertisement.includeContactDataInDefaultAnswer') }}
                </coozzy-form-checkbox>
              </div>
              <div class="col-12">
                <coozzy-form-checkbox
                  v-model="defaultAnswer.includeApplicationFormLink"
                  data-cy="marketing-edit-advert-include-application-form-link-checkbox"
                  :initial="defaultAnswer.includeApplicationFormLink"
                  name="includeApplicationformLinkInDefaultAnswer">
                  {{ $t('message.advertisement.includeApplicationformLinkInDefaultAnswer') }}
                </coozzy-form-checkbox>
              </div>
              <div class="col-12">
                <coozzy-form-checkbox
                  v-model="includeViewingAppointmentsLink"
                  data-cy="marketing-edit-advert-include-viewing-appointments-link-checkbox"
                  :initial="includeViewingAppointmentsLink"
                  :disabled="viewingAppointmentsLinkCheckboxDisabled"
                  name="includeViewingAppointmentsLinkInDefaultAnswer">
                  {{ $t('message.advertisement.includeViewingAppointmentsLinkInDefaultAnswer') }}
                  <coozzy-info-circle-icon
                    v-if="viewingAppointmentsLinkCheckboxDisabled"
                    v-b-tooltip.hover.html="$t('message.advertisement.includeViewingAppointmentsLinkDisabledTooltip')"
                    class="ml-1" />
                </coozzy-form-checkbox>
              </div>
              <div class="col-sm-12 col-md-12 mb-3">
                <label>{{ $t('message.marketingMessages.mail.mailbox') + '*' }}</label>
                <coozzy-form-select
                  v-model="defaultAnswer.mailboxId"
                  :state="$v.defaultAnswer?.mailboxId.$dirty && $v.defaultAnswer.mailboxId.$error ? false : null">
                  <option
                    v-for="mailbox in mailboxes"
                    :key="mailbox.id"
                    :value="mailbox.id">
                    {{ mailbox.outboundEmail }}
                  </option>
                </coozzy-form-select>
              </div>
              <div class="col-sm-12 col-md-12 mb-3">
                <label>{{ $t('message.settings.template.title') }}</label>
                <coozzy-form-select v-model="defaultAnswer.templateId">
                  <option :value="null" />
                  <option
                    v-for="template in sortedTemplate"
                    :key="template.id"
                    :value="template.id">
                    {{ template.name }}
                  </option>
                </coozzy-form-select>
              </div>
              <div class="col-sm-12 col-md-12 mb-3">
                <coozzy-form-input
                  v-model="defaultAnswer.subject"
                  :name="$t('message.marketingMessages.mail.subject') + '*'"
                  :state="$v.defaultAnswer?.subject.$dirty && $v.defaultAnswer.subject.$error ? false : null" />
              </div>
              <div
                class="col-sm-12 col-md-12 mb-3">
                <label for="description">{{ $t('message.marketingMessages.mail.body') + '*' }}</label>
                <editor
                  ref="editor-tiny"
                  v-model="defaultAnswer.text"
                  :api-key="editorApi"
                  :init="{
                    toolbar: 'undo redo | bold italic underline forecolor | fontsize | numlist bullist link table | fullscreen print',
                    plugins: 'fullscreen lists code image table link',
                    height: '370',
                    menubar: '',
                    language: currentLanguage,
                    elementpath: false,
                    invalid_styles: {
                      '*': 'font-family',
                    },
                    automatic_uploads: true,
                    license_key: 'gpl',
                    content_style: contentStyle,
                    font_formats: fontFormats,
                    branding: false,
                    remove_trailing_brs: false,
                    browser_spellcheck: true,
                    table_toolbar: '',
                    font_size_formats: fontsizeFormats,
                    convert_urls: false,
                    relative_urls: false,
                    remove_script_host: false
                  }" />
              </div>
              <div class="col-sm-12 col-md-12 mb-2">
                <section class="mt-2 mb-2">
                  <div
                    v-b-toggle="'collapse-placeholder'"
                    class="collapse-title">
                    <label class="cursor-pointer width-only-content">
                      {{ $t('message.generic.placeholder') }}
                    </label>
                  </div>
                  <b-collapse
                    :id="'collapse-placeholder'"
                    :visible="false"
                    class="col">
                    <p class="mb-0">
                      <span>#anrede# <br>
                        #obj.str# <br>
                        #obj.nr# <br>
                        #obj.plz# <br>
                        #obj.ort# <br>
                        #obj.zimmer# <br>
                        #obj.etage#
                      </span>
                    </p>
                  </b-collapse>
                </section>
              </div>
              <div class="col-sm-12 col-md-12 mb-3">
                <label>{{ $t('message.calendar.event.attachment') }}</label>
                <coozzy-form-file-input
                  id="fileImage"
                  ref="mediaUpload"
                  accept="text/*, application/*, image/*, video/*, .doc, .docx, .xls, .xlsx, .ppt, .pptx"
                  @change="addFile" />
                <div class="row">
                  <div
                    v-for="medium in media"
                    :key="medium.id"
                    class="media-block col-2">
                    <div
                      v-if="['image/jpeg', 'image/png', 'image/gif'].indexOf(medium.mimeType) !== -1"
                      :style="{ 'background-image': 'url(' + medium.url + ')' }"
                      class="uploaded-file my-2"
                      style="background-repeat: no-repeat;background-size: cover;background-position: center center;cursor:pointer;"
                      @click="imageClicked(medium)">
                      <coozzy-button
                        class="delete-icon"
                        design="transparent"
                        @click="removeAttachment(medium.id)">
                        <coozzy-delete-icon />
                      </coozzy-button>
                    </div>
                    <div
                      v-else
                      class="file-block p-1 my-2">
                      <a
                        :href="medium.url"
                        target="_blank">
                        {{ medium.filename }}
                      </a>
                      <coozzy-button
                        class="delete-icon"
                        design="transparent"
                        @click="removeAttachment(medium.id)">
                        <coozzy-delete-icon />
                      </coozzy-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </coozzy-card>
    </template>
  </div>
</template>

<script>
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner'
import CoozzyCard from '@/framework/components/card/CoozzyCard'
import Vue from 'vue'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect'
import { mapActions, mapGetters, mapState } from 'vuex'
import { user } from '@/mixins/user'
import { obj } from '@/mixins/object'
import { html } from '@/mixins/html'
import { required } from 'vuelidate/lib/validators'
import MessageApi from '@/misc/apis/MessageApi'
import ContactApi from '@/misc/apis/ContactApi'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox'
import ViewingAppointmentContactSection from '@/marketing/components/ViewingAppointmentContactSection'
import CoozzyFormFileInput from '@/framework/components/form/fileInput/CoozzyFormFileInput'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import CoozzyDeleteIcon from '@/framework/components/icons/CoozzyDeleteIcon'
import CoozzyInfoCircleIcon from '@/framework/components/icons/CoozzyInfoCircleIcon.vue'
import MediaApi from '@/misc/apis/MediaApi'
import CalendarSchedulerApi from '@/misc/apis/CalendarSchedulerApi'
import Editor from '@tinymce/tinymce-vue'
import { media } from '@/mixins/media'
import { validation } from '@/mixins/validation'
import { dateUtils } from '@/mixins/dateUtils'
import { exceedsOutgoingEmailAttachmentLimit, outgoingEmailAttachmentLimitI18nKey } from '@/misc/constants'

export default {
  name: 'MarketingEditAdvertCommunicationTab',
  components: {
    CoozzyDeleteIcon,
    CoozzyInfoCircleIcon,
    CoozzyButton,
    CoozzyFormFileInput,
    CoozzyFormCheckbox,
    CoozzyFormSelect,
    ViewingAppointmentContactSection,
    CoozzyCard,
    CoozzySpinner,
    Editor
  },
  mixins: [obj, user, html, media, validation, dateUtils],
  props: {
    object: {
      type: Object,
      default: null,
      required: false
    },
    building: {
      type: Object,
      default: null,
      required: false
    }
  },
  data() {
    return {
      galleryImages: [],
      employeeContacts: [],
      mailboxesLoaded: false,
      mailboxes: [],
      contactIdAssigneeMarketing: '',
      publishAssigneePhone: false,
      defaultAnswerType: 'Template',
      templateInitialized: false,
      viewingContactsResolved: false,
      attachment: null,
      media: [],
      totalFilesSize: 0,
      includeViewingAppointmentsLink: false,
      hasViewingAppointments: false,
      viewingAppointmentsLoaded: false,
      viewingAppointmentsRegisterToken: '',
      loadingViewingAppointmentsToken: false,
      defaultAnswer: {
        includeViewingAppointmentContact: false,
        includeApplicationFormLink: false,
        templateId: '',
        text: '',
        subject: '',
        mailboxId: '',
        attachmentMediaIds: []
      }
    }
  },
  computed: {
    ...mapState('message', ['templates']),
    ...mapGetters('message', ['getSignatureForUserId']),
    fontSetting() {
      return this.$store.getters['user/getFontSettings']
    },
    contentStyle() {
      const defaultFontFamily = 'Lato, sans-serif'
      const fontFamily = this.fontSetting?.family || defaultFontFamily
      const fontSize = `${this.fontSetting?.size || 10}pt`
      const color = this.fontSetting?.color
      const fontColor = color ? `rgba(${color.red}, ${color.green}, ${color.blue}, ${color.alpha})` : '#495057'
      return `body { color: ${fontColor} !important; font-family: ${fontFamily}; font-size: ${fontSize};} p {margin: 0}`
    },
    fontsizeFormats() {
      let sizeOptions = []
      for (let i = 4; i <= 36; i++) {
        sizeOptions.push(`${i}pt`)
      }
      return sizeOptions.join(' ')
    },
    fontFormats() {
      const defaultFont = 'Lato=Lato, sans-serif;'
      const userFont = this.fontSetting && this.fontSetting.family !== '' ? `${this.fontSetting.family}=${this.fontSetting.family}, sans-serif;` : defaultFont
      return `${userFont}`
    },
    sortedTemplate() {
      return this.templates.filter(x => x.type === 'TEMPLATE_TYPE_EMAIL').slice().sort((a, b) => {
        if (a.name > b.name) {
          return 1
        }
        if (b.name > a.name) {
          return -1
        }
        return 0
      })
    },
    loading() {
      return !this.object && this.mailboxesLoaded
    },
    availableEmployees() {
      return this.getEmployees().filter(e => !e.blocked && !e.deleted)
    },
    availableEmployeeContacts() {
      return this.availableEmployees
        .map(e => this.employeeContacts.find(c => c.email === e.email))
        .filter(e => !!e)
    },
    userIdAssigneeMarketing() {
      const contact = this.availableEmployeeContacts.find(e => e.id === this.contactIdAssigneeMarketing)
      let employeeUserId = ''
      if (contact) {
        employeeUserId = this.availableEmployees.find(e => e.email === contact.email)?.id || ''
      }
      return employeeUserId
    },
    editorApi() {
      return 'gpl'
    },
    getSignatureId() {
      return this.mailboxes.find(x => x.id === this.defaultAnswer.mailboxId)?.signatureId
    },
    outgoingEmailMailboxType() {
      return this.mailboxes.find(x => x.id === this.defaultAnswer.mailboxId)?.mailboxType || null
    },
    AllDataLoaded() {
      return this.mailboxesLoaded &&
        this.contactIdAssigneeMarketing &&
        this.viewingContactsResolved
    },
    viewingContactSection() {
      return this.$refs.viewingContactSection
    },
    isViewingContactSelected() {
      // Touch reactive object fields so this recomputes when contact selection changes.
      const contactId = this.object?.contacts?.contactIdViewingAppointment || ''
      const email = this.object?.viewingAppointment?.email
      const type = this.object?.viewingAppointment?.contactType || 'VIEWING_APPOINTMENT_CONTACT_TYPE_ASSIGNEE'
      const section = this.viewingContactSection
      if (section && this.viewingContactsResolved) {
        return section.isContactSelectionComplete
      }
      if (type === 'VIEWING_APPOINTMENT_CONTACT_TYPE_FREE_TEXT') {
        return !!(email || '').trim()
      }
      return !!contactId
    },
    viewingAppointmentsLinkCheckboxDisabled() {
      if (this.includeViewingAppointmentsLink) {
        return false
      }
      return !this.viewingAppointmentsLoaded || !this.hasViewingAppointments
    }
  },
  watch: {
    object: function (newVal, oldVal) {
      if (!oldVal) {
        this.viewingContactsResolved = false
        if (newVal?.contacts?.contactIdAssigneeMarketing) {
          this.contactIdAssigneeMarketing = newVal?.contacts?.contactIdAssigneeMarketing || ''
        }

        this.publishAssigneePhone = newVal?.contacts?.publishAssigneePhone || false

        if (!newVal.defaultAnswer || newVal.defaultAnswer.mailboxId === '') {
          this.defaultAnswerType = 'None'
        }

        if (newVal?.defaultAnswer) {
          this.defaultAnswer = newVal.defaultAnswer
          this.includeViewingAppointmentsLink = this.defaultAnswerTextHasViewingAppointmentsLink(newVal.defaultAnswer.text)
          if (this.includeViewingAppointmentsLink) {
            this.viewingAppointmentsRegisterToken = this.extractViewingAppointmentsTokenFromText(newVal.defaultAnswer.text)
          }
        }
        this.loadViewingAppointmentsAvailability()
      }
    },
    AllDataLoaded: function () {
      setTimeout(() => {
        this.templateInitialized = true
        this.refreshDefaultAnswerFromViewingContacts()
      }, 1000)
    },
    availableEmployees: function (newVal) {
      for (const employee of newVal) {
        for (const mailbox of this.mailboxes) {
          this.loadSignatureForUserId({
            accountId: this.accountId,
            userId: employee.id,
            signatureId: mailbox.signatureId
          })
        }
      }
    },
    defaultAnswerType: function (val) {
      if (val === 'None') {
        this.includeViewingAppointmentsLink = false
        this.viewingAppointmentsRegisterToken = ''
        this.defaultAnswer = {
          includeViewingAppointmentContact: false,
          includeApplicationFormLink: false,
          text: '',
          subject: '',
          mailboxId: '',
          attachmentMediaIds: []
        }
      } else {
        if (this.contactIdAssigneeMarketing !== '' && this.defaultAnswer.text === '') {
          this.updateDefaultAnswerMessage(this.userIdAssigneeMarketing, this.getViewingContactId(), this.getViewingContactPersonId(), this.getViewingAppointmentRandom())
        }
      }
    },
    'defaultAnswer.templateId': function (newVal) {
      if (this.templateInitialized && newVal) {
        this.renderTemplate()
      }
    },
    publishAssigneePhone: function (newVal) {
      if (this.templateInitialized) {
        this.$emit('publish-assignee-phone-updated', newVal)
      }
    },
    contactIdAssigneeMarketing: function (newVal, oldVal) {
      // load signature of mainUserId to use it on default answer
      const mailbox = this.defaultAnswer.mailboxId ? this.mailboxes.find(x => x.id === this.defaultAnswer.mailboxId) : null
      const employeeContact = this.availableEmployeeContacts.find(x => x.id === newVal)
      const employee = employeeContact ? this.availableEmployees.find(w => w.email === employeeContact.email) : null
      if (mailbox && employee && mailbox.mainUserId) {
        this.loadSignatureForUserId({
          accountId: mailbox.ownerId,
          userId: employee.id,
          signatureId: mailbox.signatureId
        })
          .then(() => {
            if (this.templateInitialized) {
              this.$emit('assignee-marketing-updated', newVal)

              const contact = this.availableEmployeeContacts.find(e => e.id === oldVal)
              let employeeUserId = ''
              if (contact) {
                employeeUserId = this.availableEmployees.find(e => e.email === contact.email)?.id || ''
              }
              this.updateDefaultAnswerMessage(employeeUserId, this.getViewingContactId(), this.getViewingContactPersonId(), this.getViewingAppointmentRandom())
            }
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.signature'), { type: 'error' })
          })
      } else {
        if (this.templateInitialized) {
          this.$emit('assignee-marketing-updated', newVal)

          const contact = this.availableEmployeeContacts.find(e => e.id === oldVal)
          let employeeUserId = ''
          if (contact) {
            employeeUserId = this.availableEmployees.find(e => e.email === contact.email)?.id || ''
          }
          this.updateDefaultAnswerMessage(employeeUserId, this.getViewingContactId(), this.getViewingContactPersonId(), this.getViewingAppointmentRandom())
        }
      }
    },
    'defaultAnswer.mailboxId': function (newVal, oldVal) {
      if (oldVal && newVal) {
        const oldMailboxType = this.mailboxes.find(x => x.id === oldVal)?.mailboxType || null
        if (exceedsOutgoingEmailAttachmentLimit(this.totalFilesSize, 0, this.outgoingEmailMailboxType) &&
          !exceedsOutgoingEmailAttachmentLimit(this.totalFilesSize, 0, oldMailboxType)) {
          Vue.toasted.show(this.$tc(outgoingEmailAttachmentLimitI18nKey(this.outgoingEmailMailboxType)), { type: 'error' })
        }
      }
      // load signature of mainUserId to use it on default answer
      const mailbox = this.defaultAnswer.mailboxId ? this.mailboxes.find(x => x.id === newVal) : null
      const employeeContact = this.availableEmployeeContacts.find(x => x.id === this.contactIdAssigneeMarketing)
      const employee = employeeContact ? this.availableEmployees.find(w => w.email === employeeContact.email) : null
      if (mailbox && employee && mailbox.mainUserId) {
        this.loadSignatureForUserId({
          accountId: mailbox.ownerId,
          userId: employee.id,
          signatureId: mailbox.signatureId
        })
          .then(() => {
            if (this.defaultAnswer.text === '') {
              this.defaultAnswer.text = '<br><section id="signature">' + (this.getSignatureForUserId(this.userIdAssigneeMarketing, this.getSignatureId)).replaceAll('<br />', '<br>').replaceAll('\n', '') + '</section>'
            }
            if (this.templateInitialized && newVal) {
              this.updateDefaultAnswerMessage(this.userIdAssigneeMarketing, this.getViewingContactId(), this.getViewingContactPersonId(), this.getViewingAppointmentRandom())
            }
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.signature'), { type: 'error' })
          })
      } else {
        if (this.defaultAnswer.text === '') {
          this.defaultAnswer.text = '<br><section id="signature">' + (this.getSignatureForUserId(this.userIdAssigneeMarketing, this.getSignatureId)).replaceAll('<br />', '<br>').replaceAll('\n', '') + '</section>'
        }
        if (this.templateInitialized && newVal) {
          this.updateDefaultAnswerMessage(this.userIdAssigneeMarketing, this.getViewingContactId(), this.getViewingContactPersonId(), this.getViewingAppointmentRandom())
        }
      }
    },
    'defaultAnswer.includeViewingAppointmentContact': function () {
      if (this.templateInitialized) {
        this.updateDefaultAnswerMessage(this.userIdAssigneeMarketing, this.getViewingContactId(), this.getViewingContactPersonId(), this.getViewingAppointmentRandom())
      }
    },
    'defaultAnswer.includeApplicationFormLink': function () {
      if (this.templateInitialized) {
        this.updateDefaultAnswerMessage(this.userIdAssigneeMarketing, this.getViewingContactId(), this.getViewingContactPersonId(), this.getViewingAppointmentRandom())
      }
    },
    includeViewingAppointmentsLink: function (newVal) {
      if (!this.templateInitialized) {
        return
      }
      if (newVal) {
        this.ensureViewingAppointmentsTokenAndRefresh()
      } else {
        this.viewingAppointmentsRegisterToken = ''
        this.updateDefaultAnswerMessage(this.userIdAssigneeMarketing, this.getViewingContactId(), this.getViewingContactPersonId(), this.getViewingAppointmentRandom())
      }
    },
    'defaultAnswer.attachmentMediaIds': function (newVal) {
      this.media = []

      if (newVal.length > 0) {
        const promises = newVal.map(mediaId => MediaApi.getMediaByID(mediaId))

        Promise.all(promises)
          .then(responses => {
            this.media = responses.map(e => e.media)
            this.totalFilesSize = this.media.reduce((sum, m) => sum + (m.size || 0), 0)
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
          })
      } else {
        this.totalFilesSize = 0
      }
    },
    defaultAnswer: {
      deep: true,
      handler: function (newVal) {
        this.$emit('default-answer-updated', newVal)
        this.$emit('empty-default-answer', this.defaultAnswerType === 'None')
      }
    },
    'defaultAnswer.text': function (newVal) {
      // check if editor has class 'is-invalid' and defaultAnswer.text not empty (class 'is-invalid' already added in validation function)
      const hasInvalide = document.getElementsByClassName('tox tox-tinymce')[0]?.classList.value.includes('is-invalid')
      if (newVal !== '' && hasInvalide) {
        document.getElementsByClassName('tox tox-tinymce')[0]?.classList.remove('is-invalid')
      }
    }
  },
  mounted() {
    this.loadEmployees()
    this.loadEmployeeContacts()
    setTimeout(() => {
      this.loadAllMailboxes()
      this.loadTemplateList(this.accountId)
      this.updateEditorContentStyle()
    }, 1000)
  },
  methods: {
    ...mapActions('employee', ['loadEmployees']),
    ...mapGetters('employee', ['getEmployees']),
    ...mapActions('message', ['loadSignatureForUserId', 'loadTemplateList']),
    async addFile(event) {
      const files = await this.getFilesFromEvent(event)
      if (files && files.length > 0) {
        const file = files[0]
        const newFileSize = (file && (typeof file.size === 'number' ? file.size : (file.size || 0))) || 0
        const limitExceeded = exceedsOutgoingEmailAttachmentLimit(this.totalFilesSize, newFileSize, this.outgoingEmailMailboxType)
        if (limitExceeded) {
          Vue.toasted.show(this.$tc(outgoingEmailAttachmentLimitI18nKey(this.outgoingEmailMailboxType)), { type: 'error' })
        } else {
          this.attachment = file
          await this.uploadAttachment()
        }
      }
      this.$nextTick(() => {
        if (this.$refs.mediaUpload) {
          this.$refs.mediaUpload.resetPlaceholder()
        }
      })
    },
    updateEditorContentStyle() {
      if (this.$refs['editor-tiny']) {
        const editor = this.$refs['editor-tiny'].editor
        if (editor?.getBody()) {
          editor.getBody().style.fontFamily = this.fontSetting.family || 'Lato'
          editor.getBody().style.fontSize = (this.fontSetting.size || 10) + 'pt'
          editor.getBody().style.color = this.fontSetting.color || '#495057'
        }
      }
    },
    getViewingContactId() {
      return this.viewingContactSection?.selectedViewingContactId || ''
    },
    getViewingContactPersonId() {
      return this.viewingContactSection?.selectedViewingContactPersonId || ''
    },
    getViewingAppointmentRandom() {
      return this.viewingContactSection?.viewingAppointmentRandom || {
        firstName: '',
        lastName: '',
        phone: '',
        email: ''
      }
    },
    onViewingAppointmentUpdated(viewingAppointment) {
      this.$emit('viewing-appointment-updated', viewingAppointment)
    },
    onViewingContactUpdated(contactId) {
      if (this.templateInitialized) {
        this.$emit('viewing-contact-updated', contactId)
      }
    },
    onContactSelectionChanged({ oldContactId, oldContactPersonId }) {
      if (this.templateInitialized) {
        this.updateDefaultAnswerMessage(
          this.userIdAssigneeMarketing,
          oldContactId,
          oldContactPersonId,
          this.getViewingAppointmentRandom()
        )
      }
    },
    onViewingRandomChanged({ oldVal }) {
      if (this.templateInitialized) {
        this.updateDefaultAnswerMessage(
          this.userIdAssigneeMarketing,
          this.getViewingContactId(),
          this.getViewingContactPersonId(),
          oldVal
        )
      }
    },
    onViewingContactsResolved() {
      this.viewingContactsResolved = true
      this.refreshDefaultAnswerFromViewingContacts()
    },
    refreshDefaultAnswerFromViewingContacts() {
      if (this.templateInitialized && this.defaultAnswerType !== 'None') {
        if (this.includeViewingAppointmentsLink && !this.viewingAppointmentsRegisterToken) {
          this.ensureViewingAppointmentsTokenAndRefresh()
          return
        }
        this.updateDefaultAnswerMessage(
          this.userIdAssigneeMarketing,
          this.getViewingContactId(),
          this.getViewingContactPersonId(),
          this.getViewingAppointmentRandom()
        )
      }
    },
    loadEmployeeContacts() {
      ContactApi.listEmployees(this.accountId)
        .then(response => {
          this.employeeContacts = response.persons
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
        })
    },
    loadAllMailboxes() {
      MessageApi.listMailboxes(this.accountId)
        .then(response => {
          this.mailboxes = response.mailboxes.filter(x => x.active === true)
          this.mailboxesLoaded = true
          this.$nextTick(() => {
            if (this.defaultAnswer.text === '') {
              this.defaultAnswer.text = this.getSignatureForUserId(this.userId, this.getSignatureId)
            }
          })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.mailbox'), { type: 'error' })
        })
    },
    applicationFormLink() {
      return `${process.env.VUE_APP_BASE_API_URL}/object/${this.object.id}`
    },
    viewingAppointmentsLink() {
      if (!this.viewingAppointmentsRegisterToken) {
        return ''
      }
      return `${process.env.VUE_APP_BASE_API_URL}/viewing-appointments/${this.viewingAppointmentsRegisterToken}`
    },
    viewingAppointmentsLinkSectionHtml() {
      const link = this.viewingAppointmentsLink()
      if (!link) {
        return ''
      }
      const label = this.$t('message.advertisement.viewingAppointmentsLinkLabel')
      return `<section id="viewingAppointmentsLink"><br>${label} ${link}<br></section>`
    },
    defaultAnswerTextHasViewingAppointmentsLink(text) {
      if (!text) {
        return false
      }
      return text.includes('id="viewingAppointmentsLink"') || text.includes('/viewing-appointments/')
    },
    extractViewingAppointmentsTokenFromText(text) {
      if (!text) {
        return ''
      }
      const match = text.match(/\/viewing-appointments\/([^"'<\s]+)/)
      return match?.[1] || ''
    },
    loadViewingAppointmentsAvailability() {
      if (!this.object?.id) {
        this.hasViewingAppointments = false
        this.viewingAppointmentsLoaded = true
        return
      }
      this.viewingAppointmentsLoaded = false
      CalendarSchedulerApi.listAppointmentsByObjectId(this.object.id)
        .then(response => {
          this.hasViewingAppointments = (response.appointments || []).some(appointment => this.isActiveViewingAppointment(appointment))
        })
        .catch(e => {
          console.log(e)
          this.hasViewingAppointments = false
        })
        .finally(() => {
          this.viewingAppointmentsLoaded = true
        })
    },
    isActiveViewingAppointment(appointment) {
      if (!appointment || appointment.status === 'APPOINTMENT_STATUS_CANCELLED') {
        return false
      }
      return !this.isViewingAppointmentInPast(appointment)
    },
    isViewingAppointmentInPast(appointment) {
      if (!appointment.date || !appointment.endTime) {
        return false
      }
      const dateStr = this.objectToIsoDate(appointment.date)
      if (!dateStr) {
        return false
      }
      const hours = appointment.endTime.hours ?? 0
      const minutes = appointment.endTime.minutes ?? 0
      const seconds = appointment.endTime.seconds ?? 0
      const end = new Date(`${dateStr}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`)
      return end.getTime() < Date.now()
    },
    ensureViewingAppointmentsTokenAndRefresh() {
      if (!this.object?.id || this.loadingViewingAppointmentsToken) {
        return
      }
      if (this.viewingAppointmentsRegisterToken) {
        this.updateDefaultAnswerMessage(this.userIdAssigneeMarketing, this.getViewingContactId(), this.getViewingContactPersonId(), this.getViewingAppointmentRandom())
        return
      }
      this.loadingViewingAppointmentsToken = true
      CalendarSchedulerApi.getTokenForRegistering(this.object.id)
        .then(response => {
          this.viewingAppointmentsRegisterToken = response.token || ''
          this.updateDefaultAnswerMessage(this.userIdAssigneeMarketing, this.getViewingContactId(), this.getViewingContactPersonId(), this.getViewingAppointmentRandom())
        })
        .catch(e => {
          console.log(e)
          this.includeViewingAppointmentsLink = false
          Vue.toasted.show(this.$t('message.savingErrors.general'), { type: 'error' })
        })
        .finally(() => {
          this.loadingViewingAppointmentsToken = false
        })
    },
    renderTemplate() {
      // Reset media
      this.media = []
      this.defaultAnswer.attachmentMediaIds = []

      MessageApi.renderTemplate(this.defaultAnswer.templateId, '')
        .then(response => {
          this.defaultAnswer.subject = response.subject
          const template = this.templates.find(e => e.id === this.defaultAnswer.templateId)
          this.defaultAnswer.attachmentMediaIds = template.attachmentMediaIds

          this.defaultAnswer.text = response.text + this.getDefaultAnswerSuffix()
          this.$refs['editor-tiny']?.editor?.fire('focusin') // refresh v-model
          this.$refs['editor-tiny']?.editor?.fire('focusout') // refresh v-model
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.renderTemplate'), { type: 'error' })
        })
    },
    updateDefaultAnswerMessage(oldAssigneeUserId, oldViewingMainContactId, oldViewingContactPersonId, oldRandom) {
      if (this.defaultAnswerType !== 'None') {
        this.defaultAnswer.text = this.getExtractedDefaultAnswerMessage(oldAssigneeUserId, oldViewingMainContactId, oldViewingContactPersonId, oldRandom)
        this.defaultAnswer.text = this.defaultAnswer.text + this.getDefaultAnswerSuffix()
        this.$refs['editor-tiny']?.editor?.fire('focusin') // refresh v-model
        this.$refs['editor-tiny']?.editor?.fire('focusout') // refresh v-model
      }
    },
    getDefaultAnswerSuffix() {
      let suffix = ''

      if (this.defaultAnswer.includeApplicationFormLink) {
        suffix += '<br>' + this.applicationFormLink() + '<br>'
      }
      if (this.includeViewingAppointmentsLink) {
        suffix += this.viewingAppointmentsLinkSectionHtml()
      }
      if (this.defaultAnswer.includeViewingAppointmentContact && this.getContactInfoString(this.getViewingContactId(), this.getViewingContactPersonId(), this.getViewingAppointmentRandom()).length) {
        suffix += '<section id="contactInfo"><br>' + this.getContactInfoString(this.getViewingContactId(), this.getViewingContactPersonId(), this.getViewingAppointmentRandom()) + '</section>'
      }
      if (this.getSignatureForUserId(this.userIdAssigneeMarketing, this.getSignatureId)) {
        suffix += '<br><section id="signature">' + (this.getSignatureForUserId(this.userIdAssigneeMarketing, this.getSignatureId)).replaceAll('<br />', '<br>').replaceAll('\n', '') + '</section>'
      }
      return this.replaceDiaeresis(suffix)
    },
    getExtractedDefaultAnswerMessage(assigneeUserId, viewingMainContactId, viewingContactPersonId, random) {
      let text = this.defaultAnswer.text

      const viewingAppointmentsLinkSectionStart = text.indexOf('<section id="viewingAppointmentsLink">')
      if (viewingAppointmentsLinkSectionStart !== -1) {
        const viewingAppointmentsLinkSectionEnd = text.indexOf('</section>', viewingAppointmentsLinkSectionStart)
        if (viewingAppointmentsLinkSectionEnd !== -1) {
          text = text.slice(0, viewingAppointmentsLinkSectionStart) + text.slice(viewingAppointmentsLinkSectionEnd + 10)
        }
      }

      const contactInfoStart = text.indexOf('<section id="contactInfo">')
      if (contactInfoStart !== -1) {
        const contactInfoEnd = text.indexOf('</section>', contactInfoStart)
        if (contactInfoEnd !== -1) {
          text = text.slice(0, contactInfoStart) + text.slice(contactInfoEnd + 10)
        }
      }

      text = this.decodeHtml(text.replaceAll('<br />', '<br>').replaceAll('\n', ''))
      if (this.getSignatureForUserId(assigneeUserId, this.getSignatureId).length) {
        const signature = text.slice(text.indexOf('<section id="signature">'), text.indexOf('</section>') + 10)
        text = text.replaceAll('<br>' + signature, '')
        text = text.replaceAll(signature, '')
        text = text.replaceAll('<p><br></p>', '')
      }
      if (this.getSignatureForUserId(assigneeUserId).length) {
        const signature = text.slice(text.indexOf('<section id="signature">'), text.indexOf('</section>') + 10)
        text = text.replaceAll('<br>' + signature, '')
        text = text.replaceAll(signature, '')
        text = text.replaceAll('<p><br></p>', '')
      }
      text = text.replaceAll(('<br>' + this.applicationFormLink() + '<br>'), '').replaceAll((this.applicationFormLink()), '')
      const existingViewingLink = this.viewingAppointmentsLink()
      if (existingViewingLink) {
        text = text.replaceAll(('<br>' + existingViewingLink + '<br>'), '').replaceAll(existingViewingLink, '')
      }
      text = text.replace(/\/viewing-appointments\/[^"'<\s]+/g, '')
      return this.replaceDiaeresis(text)
    },
    replaceDiaeresis(text) {
      return text.replaceAll('&uuml;', 'ü').replaceAll('&yuml;', 'ÿ').replaceAll('&ouml;', 'ö').replaceAll('&auml;', 'ä').replaceAll('&euml;', 'ë').replaceAll('&iuml;', 'ï').replaceAll('&auml;', 'ä')
    },
    async uploadAttachment() {
      const selectedFile = this.attachment ? [this.attachment] : []
      const response = await this.uploadMedia(selectedFile)
      if (response?.length > 0) {
        const media = response[0]
        this.media.push(media)
        this.defaultAnswer.attachmentMediaIds.push(media.id)
        this.totalFilesSize += (media.size || 0)
        this.attachment = null
      }
    },
    imageClicked(item) {
      if (item.url && item.url !== '') {
        this.galleryImages = [
          item.url
        ]
        this.$viewerApi({
          images: this.galleryImages,
          options: this.viewerOptions
        })
      }
    },
    removeAttachment(id) {
      this.defaultAnswer.attachmentMediaIds = this.defaultAnswer.attachmentMediaIds.filter(e => e !== id)
      this.media = this.media.filter(el => el.id !== id)
      this.totalFilesSize = this.media.reduce((sum, m) => sum + (m.size || 0), 0)
    },
    getContactInfoString(mainContactId, contactPersonId, random) {
      if (this.viewingContactSection) {
        return this.viewingContactSection.getContactInfoString(mainContactId, contactPersonId, random)
      }
      return ''
    },
    isInvalid() {
      if (this.$v) {
        this.$v.$touch()
        if (this.$v.$invalid) {
          if (this.$v.defaultAnswer.text.$dirty && this.$v.defaultAnswer.text.$error) {
            document.getElementsByClassName('tox tox-tinymce')[0].classList.add('is-invalid')
          }
          this.scrollToError()
        }
        if (this.$v.$invalid) {
          return true
        }
      }
      if (exceedsOutgoingEmailAttachmentLimit(this.totalFilesSize, 0, this.outgoingEmailMailboxType)) {
        Vue.toasted.show(this.$tc(outgoingEmailAttachmentLimitI18nKey(this.outgoingEmailMailboxType)), { type: 'error' })
        return true
      }
      return false
    }
  },
  validations() {
    let validations = {}
    if (this.defaultAnswerType !== 'None') {
      validations = {
        defaultAnswer: {
          mailboxId: {
            required
          },
          subject: {
            required
          },
          text: {
            required
          }
        }
      }
    }
    return validations
  }
}
</script>

<style lang="scss" scoped>
.media-block {
  width: 10%;
  max-width: 160px;
}

.uploaded-file {
  max-height: 100px;
  background-position: center center;
  background-size: cover;
  min-height: 5em;
}

@media (min-width: 768px) {
  .uploaded-file {
    max-height: 100px;
    min-height: 3em;
  }
}

@media (min-width: 992px) {
  .uploaded-file {
    max-height: 110px;
    min-height: 3.5em;
  }
}

@media (min-width: 1200px) {
  .uploaded-file {
    max-height: 120px;
    min-height: 5em;
  }
}

@media (min-width: 1600px) {
  .uploaded-file {
    max-height: 130px;
    min-height: 6em;
  }
}

@media (min-width: 1900px) {
  .uploaded-file {
    max-height: 130px;
    min-height: 7em;
  }
}

:deep(.is-invalid) {
  border-color: #dc3545;
}
</style>
